import { useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Search, Loader2, MapPin, CheckCircle2, Clock, UserCheck, Wrench, BadgeIndianRupee,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import SeoHead from "../components/SeoHead";
import { api, formatApiErrorDetail } from "../lib/api";

const FLOW = ["pending", "assigned", "in_progress", "completed"];
const STATUS_MAP = {
  NEW_REQUEST: "pending", PENDING: "pending", REQUESTED: "pending",
  CONFIRMED: "assigned", ASSIGNED: "assigned", ACCEPTED: "assigned",
  ON_THE_WAY: "in_progress", IN_PROGRESS: "in_progress", STARTED: "in_progress", ONGOING: "in_progress",
  COMPLETED: "completed", DONE: "completed", CLOSED: "completed",
  CANCELLED: "cancelled", CANCELED: "cancelled",
};
const mapStatus = (s) => STATUS_MAP[String(s || "").toUpperCase()] || (FLOW.includes(s) ? s : "pending");
const FLOW_META = {
  pending: { label: "Booking received", icon: Clock },
  assigned: { label: "Professional assigned", icon: UserCheck },
  in_progress: { label: "Work in progress", icon: Wrench },
  completed: { label: "Job completed", icon: CheckCircle2 },
};

export default function TrackPage() {
  const { ref: refParam } = useParams();
  const [ref, setRef] = useState(refParam || "");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);

  const fetchBooking = useCallback(async (r, p) => {
    setLoading(true);
    try {
      const { data } = await api.get("/bookings/track", { params: { ref: r, phone: p } });
      setBooking(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Not found");
      setBooking(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (!ref || !phone) { toast.error("Enter Booking ID and mobile number"); return; }
    fetchBooking(ref.trim(), phone.trim());
  };

  const status = booking ? mapStatus(booking.status) : "pending";
  const serviceName = booking ? (booking.service_name || booking.service || booking.category || "Service") : "";
  const partner = booking ? (booking.technician || booking.assigned_partner || booking.partner_name || booking.partner) : null;
  const amount = booking ? (booking.amount ?? booking.total ?? null) : null;
  const bookingNo = booking ? (booking.booking_no || booking.ref || refParam || ref) : "";
  const slot = booking ? (booking.scheduled_slot || booking.preferred_slot || "Scheduling") : "";
  const area = booking ? (booking.area || booking.locality || (booking.address && booking.address.area) || "") : "";
  const paid = booking && /paid/i.test(String(booking.payment_status || "")) && !/pending|unpaid/i.test(String(booking.payment_status || ""));
  const activeIdx = booking ? Math.max(0, FLOW.indexOf(status === "cancelled" ? "pending" : status)) : 0;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
      <SeoHead title="Track Your Booking - ApnaFix" description="Track your ApnaFix booking status and assigned professional." />
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900">Track your booking</h1>
      <p className="mt-2 text-slate-600">Enter your Booking ID and the mobile number used while booking.</p>

      <form onSubmit={submit} className="mt-6 bg-white rounded-2xl border border-slate-200 p-5 grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
        <div><Label className="text-sm">Booking ID</Label><Input data-testid="track-ref-input" value={ref} onChange={(e) => setRef(e.target.value.toUpperCase())} placeholder="e.g. AF1959" className="mt-1.5 h-11 font-mono-num" /></div>
        <div><Label className="text-sm">Mobile number</Label><Input data-testid="track-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Registered mobile" className="mt-1.5 h-11" /></div>
        <Button type="submit" data-testid="track-submit-btn" disabled={loading} className="h-11 bg-brand-orange hover:bg-orange-600 text-white font-semibold">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-1" /> Track</>}
        </Button>
      </form>

      {booking && (
        <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-6 af-fade-up" data-testid="track-result">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <span className="font-mono-num text-xs font-bold text-slate-400">#{bookingNo}</span>
              <h2 className="font-display text-xl font-bold text-slate-900">{serviceName}</h2>
              {(area || slot) && <p className="text-sm text-slate-500 inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {area}{area && slot ? " · " : ""}{slot}</p>}
            </div>
            {booking.payment_status && (
              <Badge variant="outline" className={paid ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}>{paid ? "Paid" : "Payment pending"}</Badge>
            )}
          </div>

          <div className="mt-6 space-y-0">
            {FLOW.map((s, i) => {
              const M = FLOW_META[s];
              const done = i <= activeIdx && status !== "cancelled";
              const current = i === activeIdx && status !== "cancelled";
              return (
                <div key={s} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <span className={`grid place-items-center w-9 h-9 rounded-full ${done ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-400"} ${current ? "ring-4 ring-emerald-100" : ""}`}><M.icon className="w-4 h-4" /></span>
                    {i < FLOW.length - 1 && <span className={`w-0.5 flex-1 min-h-[24px] ${i < activeIdx ? "bg-emerald-500" : "bg-slate-200"}`} />}
                  </div>
                  <div className="pb-6">
                    <p className={`font-semibold ${done ? "text-slate-900" : "text-slate-400"}`}>{M.label}</p>
                    {current && <p className="text-xs text-brand-orange font-medium">Current status</p>}
                  </div>
                </div>
              );
            })}
          </div>

          {status === "cancelled" && <p className="text-sm text-red-600 font-medium">This booking was cancelled.</p>}

          {partner && (
            <div className="mt-2 rounded-xl bg-slate-50 border border-slate-200 p-4 flex items-center gap-3">
              <span className="grid place-items-center w-10 h-10 rounded-full bg-navy text-white font-display font-bold">{String(partner).charAt(0)}</span>
              <div><p className="text-xs text-slate-500">Assigned professional</p><p className="font-semibold text-slate-900">{partner}</p></div>
            </div>
          )}

          {amount != null && (
            <div className="mt-4 rounded-xl border border-slate-200 p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Amount</p>
                <p className="font-mono-num text-xl font-bold text-slate-900 inline-flex items-center"><BadgeIndianRupee className="w-4 h-4" />{amount}</p>
              </div>
              <p className="text-xs text-slate-500 max-w-[55%] text-right">Payment is handled in the ApnaFix app by your assigned professional.</p>
            </div>
          )}
        </div>
      )}

      <p className="mt-6 text-center text-sm text-slate-500">Lost your Booking ID? <Link to="/contact" className="text-brand-orange font-medium">Contact us</Link></p>
    </div>
  );
}
